import type { ReactNode } from "react";
import React, { createContext, useContext, useState, useEffect } from "react";
import { useDealFinderTickers } from "hooks/useDealFinderTickers";
import type { DealFinderTicker } from "api/tickers/models/DealFinderTicker";

interface DealFinderContextType {
  defaultDeals: DealFinderTicker[];
  isDefaultDealsLoaded: boolean;
  isLoadingDefaultDeals: boolean;
}

const DealFinderContext = createContext<DealFinderContextType>({
  defaultDeals: [],
  isDefaultDealsLoaded: false,
  isLoadingDefaultDeals: false,
});

export const useDealFinderContext = () => useContext(DealFinderContext);

interface DealFinderProviderProps {
  children: ReactNode;
}

export const DealFinderProvider: React.FC<DealFinderProviderProps> = ({ children }) => {
  const [defaultDeals, setDefaultDeals] = useState<DealFinderTicker[]>([]);
  const [isDefaultDealsLoaded, setIsDefaultDealsLoaded] = useState(false);
  const [isLoadingDefaultDeals, setIsLoadingDefaultDeals] = useState(false);
  const { getDealFinderTickers } = useDealFinderTickers();

  // Load default deals once when the app starts
  useEffect(() => {
    const loadDefaultDeals = async () => {
      if (!isDefaultDealsLoaded && !isLoadingDefaultDeals) {
        try {
          setIsLoadingDefaultDeals(true);
          const tickers = await getDealFinderTickers({});
          setDefaultDeals(tickers);
          setIsDefaultDealsLoaded(true);
        } catch (error) {
          console.error("Error loading default deals:", error);
        } finally {
          setIsLoadingDefaultDeals(false);
        }
      }
    };

    loadDefaultDeals();
  }, [getDealFinderTickers, isDefaultDealsLoaded, isLoadingDefaultDeals]);

  return (
    <DealFinderContext.Provider value={{ defaultDeals, isDefaultDealsLoaded, isLoadingDefaultDeals }}>
      {children}
    </DealFinderContext.Provider>
  );
};
